import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { type AuthStep, AUTH_STEP_TYPE } from './auth-step'

export type NavigateFunction = (step: AuthStep) => void

interface NavigationContextValue {
  navigate: NavigateFunction
}

const NavigationContext = createContext<NavigationContextValue | null>(null)

const NOOP: NavigateFunction = () => undefined

export function useAuthNavigation(): NavigateFunction {
  const ctx = useContext(NavigationContext)
  if (!ctx) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        '[shared/auth-flows] useAuthNavigation called without a StepNavigationProvider. Navigation will be a no-op.'
      )
    }
    return NOOP
  }
  return ctx.navigate
}

function isSameStep(a: AuthStep, b: AuthStep): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

export interface StepNavigationProviderProps {
  children: ReactNode | ((step: AuthStep) => ReactNode)
  initialStep?: AuthStep
  onStepChange?: (step: AuthStep) => void
}

export function StepNavigationProvider({
  children,
  initialStep = { type: AUTH_STEP_TYPE.EMAIL },
  onStepChange,
}: StepNavigationProviderProps) {
  const [step, setStep] = useState<AuthStep>(initialStep)
  const prevStepRef = useRef(step)
  const onStepChangeRef = useRef(onStepChange)
  onStepChangeRef.current = onStepChange

  const navigate = useCallback<NavigateFunction>((newStep) => {
    setStep((prev) => (isSameStep(prev, newStep) ? prev : newStep))
  }, [])

  useEffect(() => {
    if (prevStepRef.current === step) return
    prevStepRef.current = step
    onStepChangeRef.current?.(step)
  }, [step])

  const value = useMemo(() => ({ navigate }), [navigate])

  return (
    <NavigationContext.Provider value={value}>
      {typeof children === 'function' ? children(step) : children}
    </NavigationContext.Provider>
  )
}
